/**
 * 陨坑空间网格 — 权威极坐标 Build Cell 模型。
 * 见 docs/01-game-design/spatial-and-building-system.md 与 geology-and-hazards.md。
 * 纯数据 + 纯函数，可序列化、纳入状态哈希；坐标与几何确定性，不使用浮点随机。
 */

/** 建设层。 */
export type BuildLayer = 'floor' | 'wall' | 'subsurface';

/** Build Cell 稳定 ID（逻辑坐标，不用世界坐标做存档）。 */
export interface BuildCellId {
  readonly layer: BuildLayer;
  readonly ring: number;
  readonly sector: number;
}

/** 每个 Build Cell 的属性（整数最小单位，basis points 用于比例）。 */
export interface BuildCell extends BuildCellId {
  visible: boolean;
  buildable: boolean;
  excavatable: boolean;
  /** 坡度（0–10000 basis points）。 */
  slopeBasisPoints: number;
  /** 承重能力。 */
  support: number;
  /** 裂隙程度（0–10000）。 */
  fracture: number;
  /** 污染（0–10000）。 */
  contamination: number;
  /** 温度（整数，相对单位）。 */
  temperature: number;
  /** 含水量（0–10000）。 */
  waterContent: number;
  /** 当前占用实体 ID（null=空闲）。 */
  occupiedBy: string | null;
  /** 危险等级。 */
  hazardLevel: number;
  /** 区域标识（可选）。 */
  district: string | null;
}

/** 地图配置（决定环形结构）。 */
export interface SpatialMapConfig {
  /**
   * 每环扇区数。`arcPitch` 未提供时全环通用；提供时仅作为**上限**，
   * 实际每环扇区数由 sectorsOfRing() 按固定弧长推导。
   */
  readonly sectorCount: number;
  readonly ringsPerLayer: Readonly<Record<BuildLayer, number>>;
  /** 坑底半径（世界单位，供坐标转换）。 */
  readonly floorRadius: number;
  /** 每环径向宽度（世界单位）。 */
  readonly ringWidth: number;
  /** 坑壁每 Band 高度（世界单位）。 */
  readonly wallBandHeight: number;
  /** 坑底基准高度。 */
  readonly floorBaseY: number;
  /**
   * 可选 — 单元格的目标切向弧长（世界单位）。
   *
   * 提供时，每环扇区数按半径推导，使格子弧长近似恒定：建筑是固定物理尺寸的，
   * 同一 footprint 在内环与外环必须代表同样的实际面积。不提供时退化为
   * 固定 `sectorCount`（既有行为，不影响已有存档与测试）。
   */
  readonly arcPitch?: number;
  /** 可选 — floor/subsurface 层 ring 0 的中心半径；不提供时为 ringWidth/2。 */
  readonly ringRadius0?: number;
}

/** 扇区数的量化步长：保证每环扇区数是 6 的倍数，环间取模与邻接稳定。 */
export const SECTOR_QUANTUM = 6;

/**
 * 某一环的扇区数。
 *
 * 未配置 `arcPitch` 时恒为 `sectorCount`（既有行为）。配置后按该环中心半径
 * 推导，量化到 SECTOR_QUANTUM 的倍数，并夹在 [SECTOR_QUANTUM, sectorCount]。
 * 纯函数、只依赖配置 —— 可序列化状态无需存储每环扇区数。
 */
export function sectorsOfRing(
  config: SpatialMapConfig,
  layer: BuildLayer,
  ring: number,
): number {
  if (config.arcPitch === undefined || config.arcPitch <= 0) return config.sectorCount;
  const radius = layer === 'wall'
    ? config.floorRadius + (ring + 0.5) * config.ringWidth
    : (config.ringRadius0 ?? config.ringWidth * 0.5) + ring * config.ringWidth;
  const raw = (2 * Math.PI * radius) / config.arcPitch;
  const quantized = Math.round(raw / SECTOR_QUANTUM) * SECTOR_QUANTUM;
  return Math.max(SECTOR_QUANTUM, Math.min(config.sectorCount, quantized));
}

export const DEFAULT_MAP_CONFIG: SpatialMapConfig = {
  sectorCount: 96,
  ringsPerLayer: { floor: 8, wall: 4, subsurface: 8 },
  floorRadius: 240,
  ringWidth: 30,
  wallBandHeight: 40,
  floorBaseY: 0,
};

/** 世界坐标点（转换用）。 */
export interface WorldPoint {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

/** 层的稳定遍历顺序（保证确定性）。 */
export const LAYER_ORDER: readonly BuildLayer[] = ['floor', 'wall', 'subsurface'];

/** 地图数据 — 纯可序列化（Record 而非 Map），纳入状态哈希。 */
export interface SpatialMapData {
  readonly config: SpatialMapConfig;
  /** cellKey → BuildCell，键由 cellKey() 生成。 */
  readonly cells: Record<string, BuildCell>;
}

/** 生成 cell 的稳定字符串键。 */
export function cellKey(id: BuildCellId): string {
  return `${id.layer}:${id.ring}:${id.sector}`;
}

/** 两个 ID 是否相等。 */
export function cellIdEquals(a: BuildCellId, b: BuildCellId): boolean {
  return a.layer === b.layer && a.ring === b.ring && a.sector === b.sector;
}
